/**
 * Role capability matrix (spec Appendix D), expressed once and enforced twice:
 * the API guards every mutating route with it, and the UI hides affordances the
 * current role cannot use — so a viewer never sees a button that would 403.
 */

import type { UserRole } from './enums.js';

export const PERMISSIONS = [
  'client:read',
  'client:write',
  'client:delete',
  'policy:read',
  'policy:write',
  'policy:delete',
  'policy:renew',
  'claim:read',
  'claim:write',
  'claim:delete',
  'reminder:read',
  'reminder:write',
  'reminder:delete',
  'document:read',
  'document:write',
  'document:delete',
  'report:read',
  'report:full',
  'report:export',
  'commission:read',
  'commission:write',
  'reference:write',
  'user:manage',
  'audit:read',
  'import:run',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const AGENT_PERMISSIONS: Permission[] = [
  'client:read',
  'client:write',
  'policy:read',
  'policy:write',
  'policy:renew',
  'claim:read',
  'claim:write',
  'reminder:read',
  'reminder:write',
  'reminder:delete',
  'document:read',
  'document:write',
  // Spec Appendix D grants agents "Summary only" reporting: aggregate figures yes,
  // the full book-of-business drill-down and exports stay with admins.
  'report:read',
  'commission:read',
];

const VIEWER_PERMISSIONS: Permission[] = [
  'client:read',
  'policy:read',
  'claim:read',
  'reminder:read',
  'document:read',
  'report:read',
  'report:full',
  'commission:read',
];

export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  admin: PERMISSIONS,
  agent: AGENT_PERMISSIONS,
  viewer: VIEWER_PERMISSIONS,
};

export function can(role: UserRole | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function canAny(role: UserRole | null | undefined, permissions: Permission[]): boolean {
  return permissions.some((p) => can(role, p));
}
